import type { SegmenterElements } from "./segmenter-elements.js";

/**
 * The box the reader types into, held to what the tool will segment.
 *
 * The cap and the counter are one module because holding the box to the cap
 * and saying how much of it is left are the same act. A view that truncated
 * the text and left the counter alone would show a reader a number that is not
 * about the text in front of them.
 */

/**
 * How much text the tool takes, in characters.
 *
 * Measured on a laptop over Tatoeba sentences, the `standard` tier segments
 * 1,000 characters in 4.3 ms, 5,000 in 18 ms and 30,000 in 101 ms, and the
 * cost is linear in the length.
 *
 * **Segmenting and drawing 5,000 characters of running text logs no long task
 * at all**, measured in Chromium with a `PerformanceObserver` over the site's
 * own 例句 as the sample. A long task is 50 ms, so the whole of it, the
 * segmentation and 751 rows of table, stays under that.
 *
 * The worst input that fits the cap is 5,000 consecutive rare characters,
 * where every character is a distinct word and the decoder has the least to go
 * on. That is 3,639 rows and one 134 ms task. Noticeable, and it is a text
 * nobody types.
 *
 * 5,000 characters is a long news article or a short chapter, which is the
 * size of thing this tool is for. 1,000 would have been a page and a half.
 */
export const inputCap = 5000;

/** What the counter says under the box. */
function counterText(length: number): string {
  return `${length.toLocaleString()} of ${inputCap.toLocaleString()} characters`;
}

/**
 * Hold the text to the cap, and say how much of it is used.
 *
 * A paste over the cap is truncated rather than refused, because somebody who
 * pasted a whole book still wants an answer about the start of it, and a box
 * that silently rejected the paste would look broken.
 *
 * A function rather than a method, because it is the elements and nothing
 * else. Every keystroke goes through it, including the ones the debounce has
 * not let through to a segmentation yet.
 */
export function holdToCap(elements: SegmenterElements): void {
  const { input, counter } = elements;

  if (input.value.length > inputCap) {
    input.value = input.value.slice(0, inputCap);
    counter.dataset["cbCapped"] = "true";
  } else {
    delete counter.dataset["cbCapped"];
  }

  counter.textContent = counterText(input.value.length);
}
